import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Coins, Mail, Search } from 'lucide-react';
import { fastapiService } from '../../services/fastapiService';
import { ADMIN_BASE } from '../../config/adminLayers';
import { WorkspaceEmailProviderModal } from '../../components/workspaces/WorkspaceEmailProviderModal';
import { WorkspaceAddCreditsModal } from '../../components/workspaces/WorkspaceAddCreditsModal';

export const EnterpriseWorkspacesPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [emailProviderWorkspace, setEmailProviderWorkspace] = useState<{ id: string; name: string } | null>(null);
  const [addCreditsWorkspace, setAddCreditsWorkspace] = useState<{ id: string; name: string } | null>(null);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fastapiService.getWorkspaces({ page, limit, search: search || undefined });
        if (cancelled) return;
        setWorkspaces(res.data || []);
        setTotal(res.pagination?.total || 0);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load workspaces');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [page, limit, search]);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-emerald-600">Tenants</p>
        <h2 className="mt-1 text-2xl font-bold text-enterprise-900">Workspaces</h2>
        <p className="mt-1 text-sm text-enterprise-600">
          Search tenants, add credits, configure email, or open a 360° workspace overview.
        </p>
      </div>

      <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
        <div className="relative mb-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-enterprise-400" />
          <input
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            placeholder="Search by workspace name..."
            className="w-full max-w-md rounded-lg border border-enterprise-200 py-2 pl-10 pr-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <p className="py-8 text-center text-enterprise-500">Loading workspaces…</p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-enterprise-100">
              <table className="min-w-full divide-y divide-enterprise-100 text-sm">
                <thead className="bg-enterprise-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-enterprise-600">
                    <th className="px-4 py-3">Workspace</th>
                    <th className="px-4 py-3">Owner</th>
                    <th className="px-4 py-3">Storage</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-enterprise-100 bg-white">
                  {workspaces.map((w) => (
                    <tr key={w.id} className="hover:bg-enterprise-50/80">
                      <td className="px-4 py-3 font-medium text-enterprise-900">{w.name || '—'}</td>
                      <td className="px-4 py-3 text-enterprise-700">
                        {w.owner?.email || w.owner?.full_name || '—'}
                      </td>
                      <td className="px-4 py-3 text-enterprise-700">
                        {w.storage?.unlimited
                          ? `${w.storage?.used_gb ?? 0} GB · Unlimited`
                          : `${w.storage?.used_gb ?? 0} / ${w.storage?.quota_gb ?? 2} GB`}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            w.is_active !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-enterprise-100 text-enterprise-700'
                          }`}
                        >
                          {w.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-enterprise-500">
                        {w.created_at ? new Date(w.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={() => setAddCreditsWorkspace({ id: w.id, name: w.name || 'Workspace' })}
                            className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800"
                            title="Add credits to workspace"
                          >
                            <Coins className="h-4 w-4" />
                            Credits
                          </button>
                          <button
                            type="button"
                            onClick={() => setEmailProviderWorkspace({ id: w.id, name: w.name || 'Workspace' })}
                            className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900"
                            title="Configure email sender (Resend/Mailgun)"
                          >
                            <Mail className="h-4 w-4" />
                            Email
                          </button>
                          <Link
                            to={`${ADMIN_BASE}/workspaces/${w.id}`}
                            className="inline-flex items-center gap-1 font-medium text-emerald-700 hover:text-emerald-900"
                          >
                            View
                            <ChevronRight className="h-4 w-4" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {workspaces.length === 0 && (
              <p className="py-8 text-center text-enterprise-500">No workspaces match your search.</p>
            )}
            <div className="mt-4 flex items-center justify-between text-sm text-enterprise-600">
              <span>
                Page {page} of {totalPages} · {total} total
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-lg border border-enterprise-200 px-3 py-1 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-lg border border-enterprise-200 px-3 py-1 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {emailProviderWorkspace && (
        <WorkspaceEmailProviderModal
          workspaceId={emailProviderWorkspace.id}
          workspaceName={emailProviderWorkspace.name}
          isOpen={!!emailProviderWorkspace}
          onClose={() => setEmailProviderWorkspace(null)}
        />
      )}
      {addCreditsWorkspace && (
        <WorkspaceAddCreditsModal
          workspaceId={addCreditsWorkspace.id}
          workspaceName={addCreditsWorkspace.name}
          isOpen={!!addCreditsWorkspace}
          onClose={() => setAddCreditsWorkspace(null)}
          onSaved={() => {}}
        />
      )}
    </div>
  );
};
